/**
 * Supported countries for WhatsApp OTP login.
 *
 * Only countries where WhatsApp is widely available are included.
 * India is the default (first entry).
 */

export interface Country {
  /** ISO 3166-1 alpha-2 code (e.g., "IN", "US") */
  code: string;
  /** Display name */
  name: string;
  /** Dial code without + (e.g., "91", "1") */
  dialCode: string;
  /** Flag emoji */
  flag: string;
  /** Expected local number length (after dial code). Array for variable-length. */
  phoneLength: number | number[];
  /** Optional: valid starting digits for local number (e.g., [6,7,8,9] for India) */
  startsWith?: number[];
  /** Example local number for placeholder */
  example: string;
}

/**
 * European sovereign states plus the transcontinental European markets that
 * users commonly expect to find in a Europe-wide selector. Kosovo uses the
 * XK user-assigned code used by international phone-number metadata.
 *
 * Calling codes, possible national-number lengths, and mobile examples are
 * aligned with Google libphonenumber metadata (reviewed 2026-07-28).
 */
export const EUROPE_COUNTRIES: Country[] = [
  { code: 'AL', name: 'Albania', dialCode: '355', flag: '🇦🇱', phoneLength: [6, 7, 8, 9], example: '672123456' },
  { code: 'AD', name: 'Andorra', dialCode: '376', flag: '🇦🇩', phoneLength: [6, 8, 9], example: '312345' },
  { code: 'AM', name: 'Armenia', dialCode: '374', flag: '🇦🇲', phoneLength: 8, example: '77123456' },
  { code: 'AT', name: 'Austria', dialCode: '43', flag: '🇦🇹', phoneLength: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13], example: '664123456' },
  { code: 'AZ', name: 'Azerbaijan', dialCode: '994', flag: '🇦🇿', phoneLength: 9, example: '401234567' },
  { code: 'BY', name: 'Belarus', dialCode: '375', flag: '🇧🇾', phoneLength: [6, 7, 8, 9, 10, 11], example: '294911911' },
  { code: 'BE', name: 'Belgium', dialCode: '32', flag: '🇧🇪', phoneLength: [8, 9], example: '450001234' },
  { code: 'BA', name: 'Bosnia and Herzegovina', dialCode: '387', flag: '🇧🇦', phoneLength: [8, 9], example: '61123456' },
  { code: 'BG', name: 'Bulgaria', dialCode: '359', flag: '🇧🇬', phoneLength: [6, 7, 8, 9, 12], example: '43012345' },
  { code: 'HR', name: 'Croatia', dialCode: '385', flag: '🇭🇷', phoneLength: [7, 8, 9], example: '921234567' },
  { code: 'CY', name: 'Cyprus', dialCode: '357', flag: '🇨🇾', phoneLength: 8, example: '96123456' },
  { code: 'CZ', name: 'Czechia', dialCode: '420', flag: '🇨🇿', phoneLength: [9, 10, 11, 12], example: '601123456' },
  { code: 'DK', name: 'Denmark', dialCode: '45', flag: '🇩🇰', phoneLength: 8, example: '34412345' },
  { code: 'EE', name: 'Estonia', dialCode: '372', flag: '🇪🇪', phoneLength: [7, 8, 10], example: '51234567' },
  { code: 'FI', name: 'Finland', dialCode: '358', flag: '🇫🇮', phoneLength: [5, 6, 7, 8, 9, 10, 11, 12], example: '412345678' },
  { code: 'FR', name: 'France', dialCode: '33', flag: '🇫🇷', phoneLength: 9, example: '612345678' },
  { code: 'GE', name: 'Georgia', dialCode: '995', flag: '🇬🇪', phoneLength: 9, example: '555123456' },
  { code: 'DE', name: 'Germany', dialCode: '49', flag: '🇩🇪', phoneLength: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], example: '15123456789' },
  { code: 'GR', name: 'Greece', dialCode: '30', flag: '🇬🇷', phoneLength: [10, 11, 12], example: '6912345678' },
  { code: 'HU', name: 'Hungary', dialCode: '36', flag: '🇭🇺', phoneLength: [8, 9], example: '201234567' },
  { code: 'IS', name: 'Iceland', dialCode: '354', flag: '🇮🇸', phoneLength: [7, 9], example: '6111234' },
  { code: 'IE', name: 'Ireland', dialCode: '353', flag: '🇮🇪', phoneLength: [7, 8, 9, 10], example: '850123456' },
  { code: 'IT', name: 'Italy', dialCode: '39', flag: '🇮🇹', phoneLength: [6, 7, 8, 9, 10, 11, 12], example: '3123456789' },
  { code: 'KZ', name: 'Kazakhstan', dialCode: '7', flag: '🇰🇿', phoneLength: [10, 14], example: '7710009998' },
  { code: 'XK', name: 'Kosovo', dialCode: '383', flag: '🇽🇰', phoneLength: [8, 9, 10, 11, 12], example: '43201234' },
  { code: 'LV', name: 'Latvia', dialCode: '371', flag: '🇱🇻', phoneLength: 8, example: '21234567' },
  { code: 'LI', name: 'Liechtenstein', dialCode: '423', flag: '🇱🇮', phoneLength: [7, 9], example: '660234567' },
  { code: 'LT', name: 'Lithuania', dialCode: '370', flag: '🇱🇹', phoneLength: 8, example: '61234567' },
  { code: 'LU', name: 'Luxembourg', dialCode: '352', flag: '🇱🇺', phoneLength: [4, 5, 6, 7, 8, 9, 10, 11], example: '628123456' },
  { code: 'MT', name: 'Malta', dialCode: '356', flag: '🇲🇹', phoneLength: 8, example: '96961234' },
  { code: 'MD', name: 'Moldova', dialCode: '373', flag: '🇲🇩', phoneLength: 8, example: '62112345' },
  { code: 'MC', name: 'Monaco', dialCode: '377', flag: '🇲🇨', phoneLength: [8, 9], example: '612345678' },
  { code: 'ME', name: 'Montenegro', dialCode: '382', flag: '🇲🇪', phoneLength: [8, 9], example: '60123456' },
  { code: 'NL', name: 'Netherlands', dialCode: '31', flag: '🇳🇱', phoneLength: [5, 6, 7, 8, 9, 10, 11], example: '612345678' },
  { code: 'MK', name: 'North Macedonia', dialCode: '389', flag: '🇲🇰', phoneLength: 8, example: '72345678' },
  { code: 'NO', name: 'Norway', dialCode: '47', flag: '🇳🇴', phoneLength: [5, 8], example: '40612345' },
  { code: 'PL', name: 'Poland', dialCode: '48', flag: '🇵🇱', phoneLength: [6, 7, 8, 9, 10], example: '512345678' },
  { code: 'PT', name: 'Portugal', dialCode: '351', flag: '🇵🇹', phoneLength: 9, example: '912345678' },
  { code: 'RO', name: 'Romania', dialCode: '40', flag: '🇷🇴', phoneLength: [6, 9], example: '712034567' },
  { code: 'RU', name: 'Russia', dialCode: '7', flag: '🇷🇺', phoneLength: [10, 14], example: '9123456789' },
  { code: 'SM', name: 'San Marino', dialCode: '378', flag: '🇸🇲', phoneLength: [8, 10], example: '66661212' },
  { code: 'RS', name: 'Serbia', dialCode: '381', flag: '🇷🇸', phoneLength: [6, 7, 8, 9, 10, 11, 12], example: '601234567' },
  { code: 'SK', name: 'Slovakia', dialCode: '421', flag: '🇸🇰', phoneLength: [6, 7, 9], example: '912123456' },
  { code: 'SI', name: 'Slovenia', dialCode: '386', flag: '🇸🇮', phoneLength: [5, 6, 7, 8], example: '31234567' },
  { code: 'ES', name: 'Spain', dialCode: '34', flag: '🇪🇸', phoneLength: 9, example: '612345678' },
  { code: 'SE', name: 'Sweden', dialCode: '46', flag: '🇸🇪', phoneLength: [6, 7, 8, 9, 10, 12], example: '701234567' },
  { code: 'CH', name: 'Switzerland', dialCode: '41', flag: '🇨🇭', phoneLength: [9, 12], example: '781234567' },
  { code: 'TR', name: 'Türkiye', dialCode: '90', flag: '🇹🇷', phoneLength: [7, 10, 12, 13], example: '5012345678' },
  { code: 'UA', name: 'Ukraine', dialCode: '380', flag: '🇺🇦', phoneLength: [9, 10], example: '501234567' },
  { code: 'GB', name: 'United Kingdom', dialCode: '44', flag: '🇬🇧', phoneLength: [7, 9, 10], example: '7400123456' },
  { code: 'VA', name: 'Vatican City', dialCode: '39', flag: '🇻🇦', phoneLength: [6, 7, 8, 9, 10, 11, 12], example: '3123456789' },
];

export const EUROPE_COUNTRY_CODES = EUROPE_COUNTRIES.map((country) => country.code);

export const COUNTRIES: Country[] = [
  // South Asia
  { code: 'IN', name: 'India', dialCode: '91', flag: '🇮🇳', phoneLength: 10, startsWith: [6, 7, 8, 9], example: '9876543210' },
  { code: 'BD', name: 'Bangladesh', dialCode: '880', flag: '🇧🇩', phoneLength: 10, example: '1712345678' },
  { code: 'NP', name: 'Nepal', dialCode: '977', flag: '🇳🇵', phoneLength: 10, example: '9812345678' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '94', flag: '🇱🇰', phoneLength: 9, example: '712345678' },

  // Gulf / Middle East
  { code: 'AE', name: 'UAE', dialCode: '971', flag: '🇦🇪', phoneLength: 9, example: '501234567' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '966', flag: '🇸🇦', phoneLength: 9, example: '512345678' },
  { code: 'QA', name: 'Qatar', dialCode: '974', flag: '🇶🇦', phoneLength: 8, example: '55123456' },
  { code: 'KW', name: 'Kuwait', dialCode: '965', flag: '🇰🇼', phoneLength: 8, example: '50012345' },
  { code: 'BH', name: 'Bahrain', dialCode: '973', flag: '🇧🇭', phoneLength: 8, example: '36001234' },
  { code: 'OM', name: 'Oman', dialCode: '968', flag: '🇴🇲', phoneLength: 8, example: '92123456' },

  // Asia-Pacific
  { code: 'SG', name: 'Singapore', dialCode: '65', flag: '🇸🇬', phoneLength: 8, example: '81234567' },
  { code: 'MY', name: 'Malaysia', dialCode: '60', flag: '🇲🇾', phoneLength: [9, 10], example: '123456789' },
  { code: 'ID', name: 'Indonesia', dialCode: '62', flag: '🇮🇩', phoneLength: [10, 11, 12], example: '8123456789' },
  { code: 'PH', name: 'Philippines', dialCode: '63', flag: '🇵🇭', phoneLength: 10, example: '9171234567' },
  { code: 'TH', name: 'Thailand', dialCode: '66', flag: '🇹🇭', phoneLength: 9, example: '812345678' },
  { code: 'AU', name: 'Australia', dialCode: '61', flag: '🇦🇺', phoneLength: 9, example: '412345678' },

  // Americas
  { code: 'US', name: 'United States', dialCode: '1', flag: '🇺🇸', phoneLength: 10, example: '2025551234' },
  { code: 'CA', name: 'Canada', dialCode: '1', flag: '🇨🇦', phoneLength: 10, example: '6135551234' },
  { code: 'BR', name: 'Brazil', dialCode: '55', flag: '🇧🇷', phoneLength: 11, example: '11987654321' },

  // Europe
  ...EUROPE_COUNTRIES,

  // Africa
  { code: 'ZA', name: 'South Africa', dialCode: '27', flag: '🇿🇦', phoneLength: 9, example: '711234567' },
  { code: 'NG', name: 'Nigeria', dialCode: '234', flag: '🇳🇬', phoneLength: 10, example: '8012345678' },
  { code: 'KE', name: 'Kenya', dialCode: '254', flag: '🇰🇪', phoneLength: 9, example: '712345678' },
];

export const DEFAULT_COUNTRY_CODE = 'IN';

/** Default country (India) */
export const DEFAULT_COUNTRY: Country =
  COUNTRIES.find((country) => country.code === DEFAULT_COUNTRY_CODE) ?? COUNTRIES[0];

/** Look up a country by ISO code */
export function getCountryByCode(code: string): Country | undefined {
  return COUNTRIES.find(c => c.code === code.toUpperCase());
}

/** Look up a country by dial code. Returns first match (US before Canada for "1"). */
export function getCountryByDialCode(dialCode: string): Country | undefined {
  return COUNTRIES.find(c => c.dialCode === dialCode);
}

/**
 * Get the minimum phone length for a country (for variable-length countries).
 */
export function getMinPhoneLength(country: Country): number {
  return Array.isArray(country.phoneLength)
    ? Math.min(...country.phoneLength)
    : country.phoneLength;
}

/**
 * Get the maximum phone length for a country.
 */
export function getMaxPhoneLength(country: Country): number {
  return Array.isArray(country.phoneLength)
    ? Math.max(...country.phoneLength)
    : country.phoneLength;
}

/**
 * Check if a given local number length is valid for the country.
 */
export function isValidPhoneLength(localDigits: string, country: Country): boolean {
  const len = localDigits.length;
  if (Array.isArray(country.phoneLength)) {
    return country.phoneLength.includes(len);
  }
  return len === country.phoneLength;
}
